import { Link } from "react-router-dom";
import {
  useNextActions,
  PRIORITY_LABEL,
  CATEGORY_LABEL,
  type NextAction,
} from "../lib/proactive";
import "../styles.proactive.css";

/**
 * "Ihre naechsten Schritte" - proaktive KI-Handlungsliste aus echten Daten (BEN-106, W2.1).
 * Rollenbewusst: Berater = Steuerung; Kunde = was liefere/entscheide ich.
 */
function ActionCard({ a }: { a: NextAction }) {
  return (
    <div className={`na-card prio-${a.priority}`}>
      <div className="na-card-head">
        <span className={`na-prio prio-${a.priority}`}>{PRIORITY_LABEL[a.priority]}</span>
        <span className="na-cat">{CATEGORY_LABEL[a.category] ?? a.category}</span>
      </div>
      <strong className="na-title">{a.title}</strong>
      <p className="na-why">
        <span className="na-why-k">Warum jetzt:</span> {a.why}
      </p>
      <p className="na-reco">{a.recommendation}</p>
      <div className="na-actions">
        <Link to={a.route} className="btn primary small" title={a.recommendation}>
          {a.action_label} &rarr;
        </Link>
        {a.intervention && (
          <Link
            to={a.intervention.route}
            className="na-iv"
            title={`Passende Intervention (verbatim aus Katalog): ${a.intervention.name}`}
          >
            <span aria-hidden>&#10022;</span> Intervention {a.intervention.code}
          </Link>
        )}
      </div>
    </div>
  );
}

export default function NextActions({
  role,
  limit = 8,
  heading,
}: {
  role: "berater" | "kunde";
  limit?: number;
  heading?: string;
}) {
  const q = useNextActions(role, { limit });
  const data = q.data;
  const title =
    heading ??
    (role === "berater"
      ? "Ihre naechsten Schritte — Steuerung"
      : "Was liefere / entscheide ich als Naechstes");

  return (
    <section className="na-panel" aria-label="Proaktive naechste Schritte">
      <div className="na-head">
        <div>
          <h2 className="na-h">{title}</h2>
          <span className="na-sub">
            Von der KI aus echten Cockpit-Daten abgeleitet &middot; priorisiert &middot; mit Beleg und Empfehlung.
          </span>
        </div>
        <span className="na-badge-ki" title="Proaktive KI-Begleitung">KI-Begleitung</span>
      </div>

      {q.isLoading && <div className="na-empty muted small">Die KI wertet Ihre aktuellen Daten aus &hellip;</div>}
      {q.isError && (
        <div className="na-empty muted small">Naechste Schritte gerade nicht abrufbar &mdash; bitte spaeter erneut.</div>
      )}
      {data && data.actions.length === 0 && !q.isLoading && (
        <div className="na-empty ok">
          Keine dringenden Punkte aus den Daten. {data.focus_today}
        </div>
      )}
      {data && data.actions.length > 0 && (
        <div className="na-grid">
          {data.actions.map((a) => (
            <ActionCard key={a.id} a={a} />
          ))}
        </div>
      )}
      {data?.degraded && (
        <p className="na-degraded small">
          Hinweis: Einzelne Datenquellen waren beim Abruf nicht vollstaendig lesbar &mdash; die Liste kann unvollstaendig sein (ehrlicher Zustand).
        </p>
      )}
    </section>
  );
}
